import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider, useAuth } from "./lib/auth";
import useContentLock from "./hooks/useContentLock";
import HomePage from "./pages/HomePage";
import After10thPathway from "./pages/After10thPathway";
import FirstYearPathway from "./pages/FirstYearPathway";
import { LegacyFutureReadyRedirect } from "./components/future-ready/LegacyFutureReadyRedirect";
import RoleDetailPage from "./pages/RoleDetailPage";
import CareersAPPage from "./pages/CareersAPPage";
import BankingRoleDetail from "./pages/BankingRoleDetail";
import BlueCollarCareerDetail from "./pages/BlueCollarCareerDetail";
import AIPathwayPage from "./pages/AIPathwayPage";
import AIDisclaimerNotice from "./components/AIDisclaimerNotice";
// Hidden (temporarily): import ParentsPage from "./pages/ParentsPage";
import InternshipPathfinder from "./pages/InternshipPathfinder";
import { FresherLaunchpad, CareerSwitchGrowth } from "./pages/ResourcePathways";
// Hidden (temporarily): import PartnersPage from "./pages/PartnersPage";
import DynamicPage from "./pages/DynamicPage";
import NotFoundPage from "./pages/NotFoundPage";
import PromptQADashboard from "./pages/projects/PromptQADashboard";
import DecisionReport from "./pages/DecisionReport";
import QuizEngine from "./pages/QuizEngine";
import CareerClarity from "./pages/CareerClarity";
import CareerClarityStart from "./pages/CareerClarityStart";
import ClarityGuidePage from "./pages/ClarityGuidePage";
import Agriculture from "./pages/Agriculture";
import After10thGroups from "./pages/After10thGroups";
import After10thQuiz from "./pages/After10thQuiz";
import SpecializationsPicker from "./pages/SpecializationsPicker";

import AIPathwayPicker from "./pages/AIPathwayPicker";
import AIPathwayPreview from "./pages/AIPathwayPreview";
import BlueCollarPicker from "./pages/BlueCollarPicker";
import BlueCollarPreview from "./pages/BlueCollarPreview";
import BankingPicker from "./pages/BankingPicker";
import BankingPreview from "./pages/BankingPreview";
import OwnPathPicker from "./pages/OwnPathPicker";
import BuildBadgesPicker from "./pages/BuildBadgesPicker";
import BuildBadgePreview from "./pages/BuildBadgePreview";
import AIRiskCheckPicker from "./pages/AIRiskCheckPicker";
import AIRiskCheckPreview from "./pages/AIRiskCheckPreview";
import OwnPathPreview from "./pages/OwnPathPreview";
import LexiLauncher from "./components/lexi/LexiLauncher";
import ClarityNudge from "./components/ClarityNudge";
import AIRoleDetail from "./pages/AIRoleDetail";
import AICareerQuiz from "./pages/AICareerQuiz";
import EntrepreneurshipPage from "./pages/EntrepreneurshipPage";
import EntrepreneurshipDetail from "./pages/EntrepreneurshipDetail";
import SchemeDetail from "./pages/SchemeDetail";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp"; // legacy — retired; kept for safety
import LifeSkillsPage from "./pages/LifeSkillsPage";
import TheAIShift from "./pages/TheAIShift";
import PreviewDetailPage from "./pages/PreviewDetailPage";
import OnboardingStagePage from "./pages/OnboardingStagePage";
import TrackVideoPage from "./pages/TrackVideoPage";
import TrackFormPage from "./pages/TrackFormPage";
import TrackQuizPage from "./pages/TrackQuizPage";
import OnboardingTrackPage from "./pages/OnboardingTrackPage";
import BuildProfilePage from "./pages/BuildProfilePage";
import AuthCallback from "./pages/AuthCallback";
import CompleteAccount from "./pages/CompleteAccount";
import ForgotPassword from "./pages/ForgotPassword";
import SiteLock from "./components/SiteLock";
import ContactPage from './pages/ContactPage';
import ReleaseReviewPage from './pages/ReleaseReviewPage';
import LexiPage from './pages/LexiPage';
import { LEXI_ENABLED } from './lib/features';
import SavedWorkPage from './pages/SavedWorkPage';
import LifeSkillDetailPage from './pages/LifeSkillDetailPage';
import { PublicDirectory } from './components/release/PublicDirectory';
import { ReadingControl } from './components/release/ReadingControl';
import { PathwayNotice } from './components/release/PathwayNotice';
import './release-v61.css';
import Dashboard from "./pages/Dashboard";
import MyCardPage from "./pages/MyCardPage";
import PublicCardPage from "./pages/PublicCardPage";
import LegalPage from "./pages/LegalPage";
import AboutPage from "./pages/AboutPage";
import SettingsPage from "./pages/SettingsPage";
import CookieBanner from "./components/CookieBanner";
import { Toaster } from "sonner";
import AIMentor from "./pages/AIMentor"; // hidden — route redirects while Lexi is off
import AIRiskIndexPage from "./pages/AIRiskIndexPage";
import AdminPanel from "./pages/AdminPanel";
import PreviewLibrary from "./pages/PreviewLibrary";
import PreviewDetail from "./pages/PreviewDetail";
import EntryChoicePage from "./pages/EntryChoicePage";
import AutomationReader from "./pages/AutomationReader";
import SpecBadgePreview from "./pages/SpecBadgePreview";
import CommunityPage from "./pages/CommunityPage";
import CommunityQuestionPage from "./pages/CommunityQuestionPage";
import OutcomesPage from "./pages/OutcomesPage";
// Hidden (temporarily): import CounsellingPage from "./pages/CounsellingPage";
import MyPathPage from "./pages/MyPathPage";
import EditPicksPage from "./pages/EditPicksPage";
import TrackGate from "./components/TrackGate";

// Simple placeholder component
const ComingSoon = ({ title }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
    <div className="text-center p-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>
      <p className="text-gray-600 mb-6">This page is under construction</p>
      <a href="/" className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors inline-block">
        Back to Home
      </a>
    </div>
  </div>
);

// Admin-only gate for /previews routes. Non-admins → /specializations (new picker).
// Keeps the OLD admin Preview Library view 100% intact for admins.
const PreviewsAdminGate = ({ children }) => {
  const { user } = useAuth() || {};
  if (user === null) return null;            // still loading
  if (user && user.is_admin) return children; // admin → keep old library
  return <Navigate to="/specializations" replace />;
};

// Redirects old specialization links to the AI-Agent Preview Library (ids map 1:1)
const SpecializationRedirect = () => {
  const { degree, id } = useParams();
  const valid = ['btech', 'bba', 'mba'].includes((degree || '').toLowerCase());
  return <Navigate to={valid && id ? `/previews/${degree.toLowerCase()}/${id}` : '/previews'} replace />;
};

// Lockdown — content-copy hardening. Activates only on copy-vulnerable routes.
const LOCK_PREFIXES = [
  '/pathways/switch/role/',
  '/previews/',
  '/explore/',
  '/explore/ai/',
  '/career-clarity/',
  '/tests/',
  '/builds/',
];
const ContentLockGate = () => {
  const location = useLocation();
  const { user } = useAuth() || {};
  const enabled = LOCK_PREFIXES.some((p) => location.pathname.startsWith(p));
  // Quiz screens stay clean — no email watermark over the questions.
  const isQuiz = location.pathname.startsWith('/career-clarity/');
  useContentLock({ enabled, watermark: isQuiz ? '' : (user?.email || 'LearnixTa preview') });
  return null;
};

function App() {
  return (
    <div className="App">
      <SiteLock>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
          <Route path="/" element={<HomePage />} />
          
          {/* Pathway Pages */}
          <Route path="/pathways/after-10th" element={<TrackGate><After10thPathway /></TrackGate>} />
          <Route path="/pathways/internships" element={<TrackGate requireOnboarding={false}><InternshipPathfinder /></TrackGate>} />
          <Route path="/pathways/fresher-launchpad" element={<TrackGate requireOnboarding={false}><FresherLaunchpad /></TrackGate>} />
          <Route path="/pathways/career-switch-growth" element={<TrackGate requireOnboarding={false}><CareerSwitchGrowth /></TrackGate>} />
          <Route path="/pathways/first-year" element={<TrackGate><FirstYearPathway /></TrackGate>} />
          <Route path="/pathways/switch" element={<TrackGate><LegacyFutureReadyRedirect /></TrackGate>} />
          <Route path="/pathways/switch/role/:catalogNumber" element={<TrackGate><LegacyFutureReadyRedirect /></TrackGate>} />
          
          {/* Explore Pages */}
          <Route path="/explore/careers-ts-ap" element={<TrackGate><CareersAPPage /></TrackGate>} />
          <Route path="/explore/careers-ts-ap/banking/:catalogNumber" element={<TrackGate><BankingRoleDetail /></TrackGate>} />
          <Route path="/explore/careers-ts-ap/blue-collar/:id" element={<TrackGate><BlueCollarCareerDetail /></TrackGate>} />
          <Route path="/explore/careers-ts-ap/:category" element={<TrackGate><CareersAPPage /></TrackGate>} />
          <Route path="/explore/ai" element={<TrackGate><AIPathwayPage /></TrackGate>} />
          <Route path="/explore/ai/quiz" element={<TrackGate><AICareerQuiz /></TrackGate>} />
          <Route path="/explore/ai/role/:id" element={<TrackGate><AIRoleDetail /></TrackGate>} />
          <Route path="/explore/entrepreneurship" element={<TrackGate><EntrepreneurshipPage /></TrackGate>} />
          <Route path="/explore/entrepreneurship/scheme/:schemeId" element={<TrackGate><SchemeDetail /></TrackGate>} />
          <Route path="/explore/entrepreneurship/:id" element={<TrackGate><EntrepreneurshipDetail /></TrackGate>} />
          
          {/* Programs */}
          <Route path="/programs/engineering" element={<ComingSoon title="Engineering Programs" />} />
          <Route path="/programs/management" element={<ComingSoon title="Management Programs" />} />
          
          {/* Projects */}
          <Route path="/projects/prompt-qa-dashboard" element={<PromptQADashboard />} />
          
          {/* Parents / Partners / Community pages are temporarily hidden.
              Page components + backend routes are intact — re-enable by
              restoring the imports and these routes. */}
          <Route path="/parents" element={<Navigate to="/" replace />} />
          <Route path="/partners" element={<Navigate to="/" replace />} />
          <Route path="/community/*" element={<Navigate to="/contact" replace />} />
          <Route path="/mentorship" element={<ComingSoon title="Mentorship" />} />
          <Route path="/mentorship/request" element={<ComingSoon title="Request Mentor" />} />
          
          {/* Testimonials */}
          <Route path="/testimonials" element={<ComingSoon title="Testimonials" />} />
          
          {/* Pricing */}
          {/* Pricing retired — Free & Open plan. Old links land safely. */}
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/checkout/:planId" element={<Navigate to="/" replace />} />
          <Route path="/unlock/:pathKey" element={<Navigate to="/dashboard" replace />} />
          
          {/* Fit Test & AI Mentor */}
          <Route path="/fit-test" element={<ComingSoon title="Psychometric Fit Test" />} />
          <Route path="/tests" element={<Navigate to="/pathways/after-10th#tests" replace />} />
          <Route path="/career-clarity" element={<TrackGate><CareerClarityStart /></TrackGate>} />
          {/* After 10th — NEW separate section. Must be registered BEFORE the
              :pathway catch-all so 'after-10th' isn't captured as a pathway. */}
          <Route path="/career-clarity/after-10th" element={<TrackGate><After10thGroups /></TrackGate>} />
          {/* Guidance + v2 exploration quiz per branch — registered BEFORE :group / :pathway */}
          <Route path="/career-clarity/after-10th/guide" element={<TrackGate><ClarityGuidePage branch="after-10th" /></TrackGate>} />
          <Route path="/career-clarity/after-inter/guide" element={<TrackGate><ClarityGuidePage branch="after-inter" /></TrackGate>} />
          <Route path="/career-clarity/after-10th/:group" element={<TrackGate><After10thQuiz /></TrackGate>} />
          <Route path="/career-clarity/:pathway" element={<TrackGate><CareerClarity /></TrackGate>} />
          <Route path="/tests/:pathway/:slug" element={<QuizEngine />} />
          <Route path="/decision-report" element={<DecisionReport />} />
          <Route path="/specializations" element={<PublicDirectory path="specializations" title="Inner Specializations" />} />          {/* Full-content detail page for a user's kept spec. Reuses the same
              admin-style renderer (PreviewDetail) so users see EVERY field. */}
          <Route path="/specializations/preview/:program/:id" element={<TrackGate><PreviewDetail /></TrackGate>} />
          <Route path="/specializations/choose/:program/:id" element={<TrackGate><EntryChoicePage pathway="specializations" /></TrackGate>} />
          <Route path="/specializations/automation/:program/:id" element={<TrackGate><AutomationReader pathway="specializations" /></TrackGate>} />
          {/* In-spec Build Badge — strict same-commit-and-lock as the Inner Specs funnel.
              No browser of other badges; back goes only to the parent preview. */}
          <Route path="/specializations/preview/:program/:id/badge" element={<TrackGate><SpecBadgePreview /></TrackGate>} />
          <Route path="/job-roles" element={<PublicDirectory path="job_roles" title="Future-Ready Job Roles" />} />
          {/* Full-content detail page for a user's kept role. Reuses the same
              admin renderer (RoleDetailPage) so users see EVERY field. */}
          <Route path="/job-roles/preview/:catalogNumber" element={<TrackGate><RoleDetailPage /></TrackGate>} />
          <Route path="/job-roles/choose/:catalogNumber" element={<TrackGate><EntryChoicePage pathway="job-roles" /></TrackGate>} />
          <Route path="/job-roles/automation/:catalogNumber" element={<TrackGate><AutomationReader pathway="job-roles" /></TrackGate>} />
          {/* PATH #4 — AI Pathway: department → roles → pick up to 3 → full preview.
              Admin pages at /explore/ai are untouched and continue to work. */}
          <Route path="/ai-pathway" element={<PublicDirectory path="ai_pathway" title="Future AI Agent Pathway" />} />
          <Route path="/ai-pathway/preview/:roleId" element={<TrackGate><AIPathwayPreview /></TrackGate>} />
          {/* PATH #6 — Blue-Collar Jobs: category → careers → pick up to 3 → full preview.
              Admin pages at /explore/careers-ts-ap/blue-collar/* are untouched. */}
          <Route path="/agriculture" element={<TrackGate><Agriculture /></TrackGate>} />
          <Route path="/blue-collar" element={<TrackGate><BlueCollarPicker /></TrackGate>} />
          <Route path="/blue-collar/preview/:careerId" element={<TrackGate><BlueCollarPreview /></TrackGate>} />
          {/* PATH #5 — Banking Careers: family → roles → pick up to 3 → full preview.
              Admin pages at /explore/careers-ts-ap/banking/* are untouched. */}
          <Route path="/banking" element={<PublicDirectory path="banking" title="Banking Careers" />} />
          <Route path="/banking/preview/:catalogNumber" element={<TrackGate><BankingPreview /></TrackGate>} />
          {/* PATH #7 — Entrepreneurship · OwnPath: budget + state → matched ideas → pick up to 3 → full preview with schemes.
              Admin /explore/entrepreneurship/* remain untouched. */}
          <Route path="/ownpath" element={<TrackGate><OwnPathPicker /></TrackGate>} />
          <Route path="/ownpath/preview/:ideaId" element={<TrackGate><OwnPathPreview /></TrackGate>} />
          {/* PATH #8 — Build Badges: program → branch → role → kept-picks → /build-badges/preview/:slug (full brief, GitHub submit).
              Auth-only (any signed-in user can open). Drill-down lock enforced server-side. */}
          <Route path="/build-badges" element={<PublicDirectory path="build_badges" title="Build Badges" />} />
          <Route path="/build-badges/preview/:slug" element={<BuildBadgePreview />} />
          {/* PATH #9 — Is AI Coming for Your Role? 30-sec check from existing AI risk data.
              Auth-only · drill-down lock enforced server-side. Picks open in a dedicated
              full-content preview page that mirrors the admin AIRiskTool layout. */}
          <Route path="/ai-risk-check" element={<PublicDirectory path="ai_risk_check" title="Is AI Coming for Your Role?" />} />
          <Route path="/ai-risk-check/preview/:roleName" element={<AIRiskCheckPreview />} />
          <Route path="/previews" element={<PreviewsAdminGate><PreviewLibrary /></PreviewsAdminGate>} />
          <Route path="/previews/:program/:id" element={<PreviewsAdminGate><PreviewDetail /></PreviewsAdminGate>} />
          <Route path="/specializations/:degree/:id" element={<SpecializationRedirect />} />
          <Route path="/specializations/:id" element={<Navigate to="/previews" replace />} />
          <Route path="/skill-audit" element={<ComingSoon title="Skill Audit" />} />
          <Route path="/ai-mentor/*" element={LEXI_ENABLED ? <LexiPage /> : <Navigate to="/" replace />} />
          <Route path="/ai-risk-index" element={<TrackGate><AIRiskIndexPage /></TrackGate>} />
          <Route path="/dashboard" element={<SavedWorkPage />} />
          <Route path="/my/card" element={<MyCardPage />} />
          <Route path="/card/:slug" element={<PublicCardPage />} />
          <Route path="/my/path" element={<MyPathPage mode="primary" />} />
          <Route path="/my/discovery" element={<MyPathPage mode="discovery" />} />
          <Route path="/my/path/edit" element={<EditPicksPage mode="primary" />} />
          <Route path="/my/discovery/edit" element={<EditPicksPage mode="discovery" />} />
          <Route path="/preview/:path_key/:preview_id" element={<PreviewDetailPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          
          {/* Tracks */}
          <Route path="/tracks" element={<ComingSoon title="Learning Tracks" />} />
          <Route path="/tracks/:track" element={<ComingSoon title="Track Detail" />} />
          
          {/* Samplers */}
          <Route path="/samplers/:lang" element={<ComingSoon title="Language Sampler" />} />
          
          {/* Auth */}
          <Route path="/signin" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/admin/login" element={<SignIn administrative />} />
          <Route path="/admin/release" element={<ReleaseReviewPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/complete-account" element={<CompleteAccount />} />
          <Route path="/signup" element={<Navigate to="/dashboard" replace />} />
          <Route path="/life-skills" element={<TrackGate requireOnboarding={false}><LifeSkillsPage /></TrackGate>} />
          <Route path="/life-skills/skills/:skillId" element={<LifeSkillDetailPage />} />
          <Route path="/the-ai-shift" element={<TheAIShift />} />
          <Route path="/education-jobs-ai" element={<Navigate to="/the-ai-shift" replace />} />
          <Route path="/signup-legacy" element={<Navigate to="/" replace />} />
          <Route path="/onboarding/stage" element={<OnboardingStagePage />} />
          <Route path="/onboarding/track" element={<OnboardingTrackPage />} />
          <Route path="/onboarding/video" element={<TrackVideoPage />} />
          <Route path="/onboarding/form" element={<TrackFormPage />} />
          <Route path="/onboarding/quiz" element={<TrackQuizPage />} />
          {/* /builds catalogue retired — redirect to Build Badges (Path 8). */}
          <Route path="/builds" element={<Navigate to="/build-badges" replace />} />
          <Route path="/builds/:slug" element={<Navigate to="/build-badges" replace />} />
          <Route path="/builds/profile/:ident" element={<BuildProfilePage />} />
          <Route path="/outcomes" element={<OutcomesPage />} />
          {/* Counselling temporarily hidden — CounsellingPage.jsx + backend kept. */}
          <Route path="/counselling" element={<Navigate to="/" replace />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Footer Pages */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />

          {/* Legal & policy pages */}
          <Route path="/terms" element={<LegalPage policy="terms" />} />
          <Route path="/privacy" element={<LegalPage policy="privacy" />} />
          <Route path="/ai-disclaimer" element={<LegalPage policy="ai-disclaimer" />} />
          <Route path="/cookies" element={<LegalPage policy="cookies" />} />
          <Route path="/copyright" element={<LegalPage policy="copyright" />} />
          <Route path="/refunds" element={<LegalPage policy="refunds" />} />
          <Route path="/acceptable-use" element={<LegalPage policy="acceptable-use" />} />
          {/* Community Guidelines hidden along with community features. */}
          <Route path="/community-guidelines" element={<Navigate to="/terms" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          
          {/* Dynamic Pages from Supabase */}
          <Route path="/pages/:slug" element={<DynamicPage />} />

          {/* Branded 404 — catch-all */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster position="top-center" />
          <CookieBanner />
          <ReadingControl />
          <PathwayNotice />
          {LEXI_ENABLED && <LexiLauncher />}
          <ClarityNudge />
        </AuthProvider>
      </BrowserRouter>
      </SiteLock>
    </div>
  );
}

export default App;
